/**
 * The catalog bridge: catalog entry → recipe document item.
 *
 * PLAN §6: *"user recipes snapshot the ingredient values at time of use
 * (recipes must not drift when the catalog updates)"*. This module is where
 * that happens, and it is the only place the two packages meet.
 *
 * What a snapshot copies is deliberately narrow: **exactly the fields the
 * engine calculates with, plus what the editor must render without a catalog
 * round-trip.** Descriptions, tags, substitutes, provenance and flavour notes
 * stay in the catalog — they are presentation, they change often, and a stale
 * copy of a description in a two-year-old recipe helps nobody.
 *
 * ## Unit and vocabulary decisions at this boundary
 *
 * | Catalog | Snapshot | Why |
 * | --- | --- | --- |
 * | `colorLovibond` **and** `colorEbc` | `colorLovibond` only | Core's canonical colour input is °L; EBC is a display conversion (`EBC = SRM × 1.97`) and storing both invites them to disagree. |
 * | no PPG (derived) | no PPG (`ppgOverride: null`) | The catalog derives PPG from extract × moisture and so does core. Copying a *derived* number would freeze a rounding, so the two inputs are copied instead. |
 * | `alphaAcidPercent: {min, max}` | `alphaAcidPercent: number` | A recipe is brewed with one lot. The band's mid-point is the honest default; the field is then the per-lot override the brewer edits off the bag. |
 * | `attenuationPercent: {min, max}` | both ends kept | The editor shows "attenuates 78–82 %, predicted FG uses 80 %" (PLAN §4.2), and core takes the band and picks the mid-point itself. |
 * | `temperatureC: {min, max}` | both ends kept | Fermentation schedule guidance; core does not read it. |
 * | `maltClass` (8 values) | core's `MaltPhClass` (7 values) | The handshake the catalog README calls out: the catalog names the class, core owns the coefficients. `base-pilsner` and `base-pale` both map to core's `base`. |
 * | `diPh` / `bufferingCapacity` (nullable) | copied verbatim, still nullable | `null` means "use the class default" in both packages. Never invent a number here — a wrong DI pH silently poisons the pH model. |
 * | `type` | core's `FermentableClass` | See {@link fermentableClassOf}. |
 */

import type { Fermentable, Hop, MaltClass, Misc, Yeast } from '@beerwright/catalog';
import { CATALOG_VERSION } from '@beerwright/catalog';
import type { CatalogRef, MiscAmountUnit } from '../document/common';
import type {
  CustomFermentableDoc,
  CustomHopDoc,
  CustomMiscDoc,
  CustomYeastDoc,
} from '../document/custom-ingredient';
import { customIngredientDisplayName } from '../document/custom-ingredient';
import type {
  RecipeFermentable,
  RecipeHop,
  RecipeMisc,
  RecipeYeast,
} from '../document/ingredients';
import { midpoint } from './range';

/** Catalog `maltClass` → core `MaltPhClass`. */
export const MALT_PH_CLASS_BY_CATALOG_CLASS: Readonly<
  Record<MaltClass, RecipeFermentable['maltClass']>
> = {
  'base-pilsner': 'base',
  'base-pale': 'base',
  'munich-vienna': 'munich-vienna',
  wheat: 'wheat-other',
  crystal: 'crystal',
  roasted: 'roasted',
  acidulated: 'acidulated',
  'non-buffering': 'none',
};

/**
 * The fermentables the catalog files under `sugar` that the yeast cannot touch.
 *
 * The catalog has no "fermentable by yeast?" field — it states product facts,
 * and "is this fermentable" is a *calculation* concern. Core does distinguish
 * (`non-fermentable` contributes gravity but no alcohol), so the mapping is
 * made here, explicitly, from an enumerated list. A test asserts the list still
 * covers everything in the shipped catalog that qualifies, so a newly added
 * lactose variant fails the build rather than quietly fermenting out.
 */
export const NON_FERMENTABLE_SUGAR_IDS: readonly string[] = ['lactose', 'maltodextrin'];

/**
 * Moisture above which an `extract` entry is treated as liquid malt extract.
 *
 * DME ships at ~3 % moisture, LME at ~20 %; there is no product in between, so
 * a 10 % threshold separates them with a wide margin and needs no id list.
 */
const LIQUID_EXTRACT_MOISTURE_THRESHOLD_PERCENT = 10;

/**
 * Catalog `type` → core `FermentableClass`.
 *
 * - grain-shaped types (`base`, `crystal-caramel`, `roasted`, `kilned-toasted`,
 *   `adjunct-grain`) become `grain`. `needsMashing: false` means the malt
 *   *can* be steeped (already-converted crystal, roast, dextrin) — the same
 *   trap as BeerSmith's `RECOMMEND_MASH` — not that it should land in the
 *   recipe as a steeping grain. Carafoam in an all-grain beer is mashed;
 *   extract brewers switch Use to Steep.
 * - `extract` splits on moisture (see the threshold above).
 * - `sugar` becomes `non-fermentable` for the enumerated exceptions, `sugar`
 *   otherwise.
 * - `fruit` becomes `sugar`: its extract is essentially fully fermentable, and
 *   core has no fruit class.
 */
export function fermentableClassOf(entry: Fermentable): RecipeFermentable['class'] {
  switch (entry.type) {
    case 'sugar':
      return NON_FERMENTABLE_SUGAR_IDS.includes(entry.id) ? 'non-fermentable' : 'sugar';
    case 'extract':
      return entry.moisturePercent >= LIQUID_EXTRACT_MOISTURE_THRESHOLD_PERCENT
        ? 'liquid-extract'
        : 'dry-extract';
    case 'fruit':
      return 'sugar';
    default:
      return 'grain';
  }
}

/**
 * Where a fermentable goes by default.
 *
 * Grain and steeping grain go in the mash (an all-grain recipe mashes its
 * crystal malt); sugars and extracts go in the boil; fruit goes in the
 * fermenter, which is where it is actually used and where its sugar does not
 * get boiled off its aroma.
 */
export function defaultFermentableUse(entry: Fermentable): RecipeFermentable['use'] {
  if (entry.type === 'fruit') return 'fermenter';
  const cls = fermentableClassOf(entry);
  return cls === 'grain' || cls === 'steeping-grain' ? 'mash' : 'boil';
}

/** Build the provenance stamp for a snapshot taken now. */
function refFor(kind: CatalogRef['kind'], id: string, catalogVersion: string): CatalogRef {
  return { kind, id, catalogVersion };
}

/** Options every snapshot function accepts. */
interface SnapshotBase {
  /** Stable id for the new recipe item. Caller-supplied — this package mints no ids. */
  readonly itemId: string;
  /**
   * Catalog version to stamp. Defaults to the linked catalog's
   * `CATALOG_VERSION`; pass it explicitly when replaying a historic snapshot.
   */
  readonly catalogVersion?: string;
}

/**
 * Snapshot a catalog fermentable into a recipe item.
 *
 * @param entry - The catalog entry, e.g. `getFermentable('rahr-standard-2-row')`.
 * @param options - Item id, amount, and any add-time overrides.
 */
export function snapshotFermentable(
  entry: Fermentable,
  options: SnapshotBase & {
    /** Amount, **kilograms**. */
    readonly amountKg: number;
    /** Where it is added; defaults to {@link defaultFermentableUse}. */
    readonly use?: RecipeFermentable['use'];
    /** Display name; defaults to `"<maker> <name>"`, or just the name for generics. */
    readonly name?: string;
  },
): RecipeFermentable {
  return {
    itemId: options.itemId,
    name: options.name ?? displayName(entry.maker, entry.name),
    catalogRef: refFor('fermentable', entry.id, options.catalogVersion ?? CATALOG_VERSION),
    ingredientId: null,
    amountKg: options.amountKg,
    class: fermentableClassOf(entry),
    use: options.use ?? defaultFermentableUse(entry),
    extractFineGrindDryBasisPercent: entry.extractFineDryPercent,
    moisturePercent: entry.moisturePercent,
    colorLovibond: entry.colorLovibond,
    diastaticPowerLintner: entry.diastaticPowerLintner,
    maltClass: MALT_PH_CLASS_BY_CATALOG_CLASS[entry.maltClass],
    distilledWaterPh: entry.diPh,
    bufferingCapacityMEqPerKgPerPh: entry.bufferingCapacity,
    acidContentPercentLactic: null,
    ppgOverride: null,
    fineCoarseDifferencePercent: null,
    fermentabilityPercentOverride: null,
    note: null,
  };
}

/**
 * Snapshot a catalog hop into a recipe item.
 *
 * `alphaAcidPercent` defaults to the mid-point of the published band and is
 * then the brewer's per-lot figure. Beta acid and total oil are also
 * mid-pointed; they are carried for the UI and read by no calculation.
 */
export function snapshotHop(
  entry: Hop,
  options: SnapshotBase & {
    /** Amount, **kilograms**. */
    readonly amountKg: number;
    /** Where it goes. */
    readonly use: RecipeHop['use'];
    /** Boil time remaining or stand duration, **minutes**. Defaults to 0. */
    readonly timeMinutes?: number;
    /**
     * Physical form. Defaults to the catalog row's own `defaultForm`, and to
     * `pellet` when it has none — which is what homebrewers actually buy, and
     * what every variety is. A product row carries its own, because adding a
     * pre-isomerised extract as a pellet would under-report its bitterness
     * fourfold and nothing on screen would say so.
     */
    readonly form?: RecipeHop['form'];
    /** This lot's alpha acid, **%**. Defaults to the published band's mid-point. */
    readonly alphaAcidPercent?: number;
    /** Dry-hop contact time, **days**. */
    readonly dryHopDays?: number;
    /** Whirlpool start temperature, **°C**. */
    readonly whirlpoolTempC?: number;
    /** Whirlpool temperature at the end of the stand, **°C**. */
    readonly whirlpoolEndTempC?: number;
  },
): RecipeHop {
  return {
    itemId: options.itemId,
    name: entry.name,
    catalogRef: refFor('hop', entry.id, options.catalogVersion ?? CATALOG_VERSION),
    ingredientId: null,
    amountKg: options.amountKg,
    alphaAcidPercent: options.alphaAcidPercent ?? midpoint(entry.alphaAcidPercent),
    form: options.form ?? entry.defaultForm ?? 'pellet',
    use: options.use,
    timeMinutes: options.timeMinutes ?? 0,
    dryHopDays: options.dryHopDays ?? null,
    whirlpoolTempC: options.whirlpoolTempC ?? null,
    whirlpoolEndTempC: options.whirlpoolEndTempC ?? null,
    betaAcidPercent: midpoint(entry.betaAcidPercent),
    totalOilMlPer100g: midpoint(entry.totalOilMlPer100g),
    isoAlphaPercent: entry.isoAlphaPercent === undefined ? null : midpoint(entry.isoAlphaPercent),
    note: null,
  };
}

/**
 * Catalog yeast `type` → core `YeastType`.
 *
 * Core's type drives one thing: the default pitch rate (PLAN §5.6). `hybrid`
 * and `kveik` both map to `ale` — a kölsch strain is pitched at ale rates even
 * though it ferments cool, and kveik is pitched *lower* than ale rates, which
 * is a pitch-rate override rather than a family. `wild` maps to `brett`.
 */
function yeastTypeOf(entry: Yeast): RecipeYeast['type'] {
  switch (entry.type) {
    case 'lager':
      return 'lager';
    case 'wild':
      return 'brett';
    default:
      return 'ale';
  }
}

/** Grams in a standard dry sachet — the only pack size the labs ship at homebrew scale. */
const DRY_SACHET_GRAMS = 11.5;

/**
 * Snapshot a catalog yeast into a recipe item.
 *
 * Cell counts are **not** invented: the catalog does not publish them, so
 * `cellsPerPackBillion` / `cellsPerGramBillion` stay `null` unless the caller
 * supplies measured figures, and core's pitch-rate maths falls back to its own
 * documented defaults. `gramsPerPack` is set to the universal 11.5 g sachet for
 * dry yeast and left `null` for liquid.
 */
export function snapshotYeast(
  entry: Yeast,
  options: SnapshotBase & {
    /** Packs/vials/sachets pitched. Defaults to 1. */
    readonly packs?: number;
    /** Manufacture date, `YYYY-MM-DD`, for viability decay. */
    readonly manufactureDate?: string;
    /** Attenuation actually expected, **%**. Defaults to the published band. */
    readonly attenuationPercentOverride?: number;
  },
): RecipeYeast {
  return {
    itemId: options.itemId,
    name: displayName(entry.lab, entry.name),
    catalogRef: refFor('yeast', entry.id, options.catalogVersion ?? CATALOG_VERSION),
    ingredientId: null,
    type: yeastTypeOf(entry),
    form: entry.form,
    attenuationMinPercent: entry.attenuationPercent.min,
    attenuationMaxPercent: entry.attenuationPercent.max,
    attenuationPercentOverride: options.attenuationPercentOverride ?? null,
    packs: options.packs ?? 1,
    cellsPerPackBillion: null,
    gramsPerPack: entry.form === 'dry' ? DRY_SACHET_GRAMS : null,
    cellsPerGramBillion: null,
    dosageGramsPerHl: entry.dosageGramsPerHl ?? null,
    manufactureDate: options.manufactureDate ?? null,
    temperatureMinC: entry.temperatureC.min,
    temperatureMaxC: entry.temperatureC.max,
    // A freshly snapshotted culture has no starter; the brewer plans one later.
    starter: null,
    note: null,
  };
}

/** Which measurement unit a misc type is normally dosed in. */
/**
 * The unit an addition is measured in, from the form it is sold in.
 *
 * Exported because a caller computing an *amount* has to know which unit that
 * amount will land in — a dose per litre means grams for a powder and
 * millilitres for a liquid, and there is no second place for that mapping to
 * live without the two drifting apart.
 */
export function miscUnitFor(form: Misc['form'] | CustomMiscDoc['form']): MiscAmountUnit {
  if (form === 'tablet') return 'each';
  return form === 'liquid' ? 'ml' : 'g';
}

/**
 * Snapshot a catalog misc into a recipe item.
 *
 * Miscs feed no calculation (see `document/ingredients.ts`), so the snapshot is
 * thin: identity, stage, dosing unit, and the solution strength that a later
 * acid conversion would need.
 */
export function snapshotMisc(
  entry: Misc,
  options: SnapshotBase & {
    /** Amount, in {@link snapshotMisc}'s resolved unit. */
    readonly amount: number;
    /** Measurement unit; defaults from the catalog's `form`. */
    readonly unit?: MiscAmountUnit;
    /** Stage; defaults to the catalog's typical `use`. */
    readonly use?: RecipeMisc['use'];
    /** Contact time, **minutes**; defaults to the catalog's typical timing. */
    readonly timeMinutes?: number;
  },
): RecipeMisc {
  return {
    itemId: options.itemId,
    name: entry.name,
    catalogRef: refFor('misc', entry.id, options.catalogVersion ?? CATALOG_VERSION),
    ingredientId: null,
    type: entry.type,
    use: options.use ?? entry.use,
    amount: options.amount,
    unit: options.unit ?? miscUnitFor(entry.form),
    timeMinutes: options.timeMinutes ?? entry.timingMinutes,
    concentrationPercent: entry.concentrationPercent,
    note: null,
  };
}

/** `"<maker> <name>"`, minus the redundancy when the maker is `Generic`. */
function displayName(maker: string, name: string): string {
  if (maker === 'Generic' || name.startsWith(maker)) return name;
  return `${maker} ${name}`;
}

/**
 * Where a custom fermentable goes by default, from its recipe class.
 *
 * Same rule as {@link defaultFermentableUse}: grain is mashed, everything else
 * is boiled. There is no fruit class on a custom document, so nothing lands in
 * the fermenter unless the brewer says so.
 */
export function defaultCustomFermentableUse(
  cls: RecipeFermentable['class'],
): RecipeFermentable['use'] {
  return cls === 'grain' || cls === 'steeping-grain' ? 'mash' : 'boil';
}

/**
 * Snapshot a custom fermentable into a recipe item.
 *
 * `catalogRef` is `null`: this is the user's library, not the shipped catalog.
 * `ingredientId` is the entry's id, and that is the *whole* of the link — the
 * numbers are copied, so editing the library later cannot move this recipe, and
 * the name is spelled the way {@link customIngredientDisplayName} spells it so
 * the row reads the same as every other row built from this entry.
 */
export function snapshotCustomFermentable(
  entry: CustomFermentableDoc,
  options: {
    readonly itemId: string;
    readonly amountKg: number;
    readonly use?: RecipeFermentable['use'];
    readonly name?: string;
  },
): RecipeFermentable {
  return {
    itemId: options.itemId,
    name: options.name ?? customIngredientDisplayName(entry),
    catalogRef: null,
    ingredientId: entry.id,
    amountKg: options.amountKg,
    class: entry.class,
    use: options.use ?? defaultCustomFermentableUse(entry.class),
    extractFineGrindDryBasisPercent: entry.extractFineGrindDryBasisPercent,
    moisturePercent: entry.moisturePercent,
    colorLovibond: entry.colorLovibond,
    diastaticPowerLintner: entry.diastaticPowerLintner,
    maltClass: entry.maltClass,
    distilledWaterPh: entry.distilledWaterPh,
    bufferingCapacityMEqPerKgPerPh: entry.bufferingCapacityMEqPerKgPerPh,
    acidContentPercentLactic: entry.acidContentPercentLactic,
    ppgOverride: null,
    fineCoarseDifferencePercent: null,
    fermentabilityPercentOverride: null,
    note: null,
  };
}

/** Snapshot a custom hop into a recipe item. */
export function snapshotCustomHop(
  entry: CustomHopDoc,
  options: {
    readonly itemId: string;
    readonly amountKg: number;
    readonly use: RecipeHop['use'];
    readonly timeMinutes?: number;
    readonly form?: RecipeHop['form'];
    readonly alphaAcidPercent?: number;
    readonly dryHopDays?: number;
    readonly whirlpoolTempC?: number;
    readonly whirlpoolEndTempC?: number;
  },
): RecipeHop {
  return {
    itemId: options.itemId,
    name: entry.name,
    catalogRef: null,
    ingredientId: entry.id,
    amountKg: options.amountKg,
    alphaAcidPercent: options.alphaAcidPercent ?? entry.alphaAcidPercent,
    form: options.form ?? 'pellet',
    use: options.use,
    timeMinutes: options.timeMinutes ?? 0,
    dryHopDays: options.dryHopDays ?? null,
    whirlpoolTempC: options.whirlpoolTempC ?? null,
    whirlpoolEndTempC: options.whirlpoolEndTempC ?? null,
    betaAcidPercent: entry.betaAcidPercent,
    totalOilMlPer100g: entry.totalOilMlPer100g,
    note: null,
  };
}

/** Snapshot a custom yeast into a recipe item. */
export function snapshotCustomYeast(
  entry: CustomYeastDoc,
  options: {
    readonly itemId: string;
    readonly packs?: number;
    readonly manufactureDate?: string;
    readonly attenuationPercentOverride?: number;
  },
): RecipeYeast {
  return {
    itemId: options.itemId,
    name: customIngredientDisplayName(entry),
    catalogRef: null,
    ingredientId: entry.id,
    type: entry.type,
    form: entry.form,
    attenuationMinPercent: entry.attenuationMinPercent,
    attenuationMaxPercent: entry.attenuationMaxPercent,
    attenuationPercentOverride: options.attenuationPercentOverride ?? null,
    packs: options.packs ?? 1,
    cellsPerPackBillion: null,
    gramsPerPack: entry.form === 'dry' ? DRY_SACHET_GRAMS : null,
    cellsPerGramBillion: null,
    // A brewer's own yeast has no maker's sheet behind it, so the dry-pitch
    // check falls back to the class-typical band for its type.
    dosageGramsPerHl: null,
    manufactureDate: options.manufactureDate ?? null,
    temperatureMinC: entry.temperatureMinC,
    temperatureMaxC: entry.temperatureMaxC,
    starter: null,
    note: null,
  };
}

/** Snapshot a custom misc into a recipe item. */
export function snapshotCustomMisc(
  entry: CustomMiscDoc,
  options: {
    readonly itemId: string;
    readonly amount: number;
    readonly unit?: RecipeMisc['unit'];
    readonly use?: RecipeMisc['use'];
    readonly timeMinutes?: number;
  },
): RecipeMisc {
  return {
    itemId: options.itemId,
    name: entry.name,
    catalogRef: null,
    ingredientId: entry.id,
    type: entry.type,
    use: options.use ?? entry.use,
    amount: options.amount,
    unit: options.unit ?? miscUnitFor(entry.form),
    timeMinutes: options.timeMinutes ?? entry.timingMinutes,
    concentrationPercent: entry.concentrationPercent,
    note: null,
  };
}
